<template>
  <section>
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <h1 class="title mr-3">{{ $t('entity.patients') }}</h1>
        <button v-if="!isNeuroAdmin && !isReadonly" class="btn btn-link btn-with-icon" @click="createPatient" data-testid="btn-add-patient">
          <span class="icon icon-plus"></span>{{ $t('src/pages/patients.8146') }}
        </button>
      </div>
      <div class="position-relative">
        <base-reset-button v-if="patientId" class="input-reset" v-model="patientId"></base-reset-button>
        <person-search-filter
          :class="'input-group-sm input-group-wide'"
          :id="patientId"
          :find-by="findByPerson"
          :find-only-by-this="true"
          @select:item="selectPatient"
        ></person-search-filter>
      </div>
    </div>

    <base-table
      :no-data="!hasPatients"
      :headers="headers"
      :loading="loading"
      :sorting="sorting"
      data-testid="table-patients"
      @sorting="changeSorting"
    >
      <template #tbody>
        <patient-table-row
          v-for="patient in patients"
          :key="patient.id"
          :data-testid="`patient-row-${patient.id}`"
          :patient="patient"
        ></patient-table-row>
      </template>
    </base-table>
    <div class="mt-3">
      <paginator
        v-if="showPaginator"
        :total="totalCount"
        :per-page="perPage"
        v-model="currentPage"
        @change:count="numberOfElements"
      ></paginator>
    </div>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { paginator } from '@/mixins/paginator.js'
import { stringify } from 'qs'
import { userRights } from '@/mixins/user-rights'
import BaseResetButton from '@/base/BaseResetButton.vue'
import { FIND_BY_PERSON } from '@/main.config'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

export default {
  name: 'PatientsList',
  mixins: [paginator, userRights, systemUnavailable],
  components: {
    BaseTable: () => import('@/base/BaseTable.vue'),
    PersonSearchFilter: () => import('@/components/Filters/PersonSearchFilter.vue'),
    PatientTableRow: () => import('@/components/table-rows/PatientTableRow.vue'),
    BaseResetButton: BaseResetButton
  },
  data: () => ({
    loading: true,
    patients: null,
    patientId: null,
    sorting: {
      by: 'name',
      order: 'asc'
    }
  }),
  computed: {
    findByPerson() {
      return FIND_BY_PERSON.patient
    },
    sortParams() {
      const sortOrder = this.sorting.order === 'asc' ? 1 : -1
      if (this.sorting.by === 'name') {
        return {
          surname: sortOrder,
          name: sortOrder,
          middle_name: sortOrder
        }
      } else if (this.sorting.by === 'birthDate') {
        return {
          birth_date: sortOrder
        }
      }
      return {
        id: 1
      }
    },
    params() {
      let params = Object.assign({}, this.pageParams)

      params['$sort'] = this.sortParams

      if (this.patientId) {
        params['id'] = this.patientId
      }
      if (this.$store.getters.SELECTED_HOSPITAL) {
        params['hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      return params
    },
    hasPatients() {
      return this.patients && this.patients.length > 0
    },
    headers() {
      let title = this.isNeuroAdmin ? `№` : this.$t('personInfo.fullName')
      return {
        name: {
          title: title,
          class: 'th-name',
          sorting: 'asc'
        },
        birthDate: {
          title: this.$t('personInfo.dateOfBirth'),
          sorting: 'asc'
        },
        phone: {
          title: this.$t('personInfo.phone'),
          class: 'th-phone'
        },
        address: {
          title: this.$t('general.address')
        },
        course: {
          title: this.$t('entity.courses')
        }
      }
    }
  },
  watch: {
    patientId() {
      this.currentPage = 1
      this.loadData()
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true

      http
        .get('/patients', {
          params: this.params,
          paramsSerializer: params =>
            stringify(params, {
              arrayFormat: 'indices',
              encodeValuesOnly: true
            })
        })
        .then(response => response.data)
        .then(data => {
          this.totalCount = data.total
          this.patients = data.data
        })
        .catch(error => console.log(error))
        .finally(() => (this.loading = false))
    },
    selectPatient(data) {
      this.patientId = data.id
    },
    changeSorting(sortingData) {
      this.sorting = sortingData
      this.loadData()
    },
    numberOfElements(number) {
      this.perPage = number
    },
    createPatient() {
      if (Number(localStorage.getItem('activeHospital'))) {
        this.$router.push({ name: 'patient-add' })
      } else {
        this.$emit('system:unavailable')
      }
    }
  }
}
</script>
